import Image from 'next/image'
import { useKeenSlider } from 'keen-slider/react'
import { FC, Suspense, lazy, useContext, useState } from 'react'
import { motion } from 'framer-motion'
import { Dots } from './components/Dots'
import { FormContainer } from '../FormContainer'

import { CalculatorSolarContext } from 'context/CalculatorSolarContext'

import 'keen-slider/keen-slider.min.css'

import houseSummaryStaticImg from '@assets/v2/house-summary-static.png'
import * as InfoCard from './lead-v2/components/info-card'
import { HomeButton } from '@components/home/components/home-button'
import { useRouter } from 'next/router'
import Link from 'next/link'

const HouseAnimation = lazy(() => import('../HouseAnimation'))

interface Props {
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p'
  compactMode?: boolean
}

export const Summary: FC<Props> = ({ headingLevel, compactMode = false }) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const router = useRouter()

  const { summaryData, handleCurrentStep, handleResetSimulation } = useContext(
    CalculatorSolarContext
  )

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    slides: {
      perView: 1,
      spacing: 16
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel)
    },
    created() {
      setLoaded(true)
    }
  })

  function asBRL(value, minimumFractionDigits = undefined) {
    return new Intl.NumberFormat('pt-BR', {
      maximumFractionDigits: 2,
      minimumFractionDigits: minimumFractionDigits
    }).format(value || '0')
  }

  return (
    <div
      className={`w-full flex md:rounded-2xl bg-v3-gray-75 md:pr-0 transition-all ${
        compactMode
          ? 'flex-col'
          : 'md:max-w-[1280px] md:gap-[3.625rem] md:max-h-[650px] lg:px-0 lg:h-full lg:max-w-full lg:grid lg:grid-cols-[1fr_428px] lg:gap-0 xl:max-h-[650px] xl:justify-between xl:max-w-[879px] xl:grid-cols-[1fr_336px] xl:pt-0 1.5xl:max-w-[1218px] 1.5xl:grid-cols-[1fr_428px]'
      }`}
    >
      <motion.div
        whileInView={{
          opacity: 1
        }}
        variants={{
          hidden: {
            opacity: 0
          }
        }}
        initial="hidden"
        transition={{
          duration: 1,
          delay: 0.5
        }}
        viewport={{
          once: true
        }}
        className={
          compactMode
            ? 'hidden'
            : 'hidden lg:w-full lg:flex lg:flex-col lg:justify-between lg:items-center lg:py-7 lg:max-w-[360px] lg:mx-auto 1.5xl:max-w-[472px]'
        }
      >
        <div className="relative overflow-hidden w-full flex justify-center items-center max-w-[270px] xl:max-w-[200px] 1.5xl:max-w-[320px]">
          <Suspense fallback={<Image src={houseSummaryStaticImg} alt="" />}>
            <HouseAnimation />
          </Suspense>
        </div>

        <div className="w-full flex flex-col gap-2 -mt-7 1.5xl:-mt-10">
          <p className="font-myriad text-lg text-v3-gray-475 uppercase md:text-xl md:tracking-wide lg:leading-none xl:text-[0.8125rem] xl:leading-none 1.5xl:text-xl 1.5xl:leading-none">
            Economia anual estimada
          </p>
          <span className="text-5xl font-bold text-v3-gray-425 md:leading-none tracking-wide xl:text-[1.9375rem] 1.5xl:text-5xl 1.5xl:leading-none">
            R$ {asBRL(summaryData?.annual_savings, 2)}
          </span>
        </div>

        <div className="lg:w-full lg:grid lg:grid-cols-2 lg:gap-4 xl:gap-[10.58px]">
          <InfoCard.Root>
            <InfoCard.Label>Payback estimado</InfoCard.Label>
            <InfoCard.Value className="uppercase">
              {Math.round(summaryData?.payback)} meses
            </InfoCard.Value>
          </InfoCard.Root>

          <InfoCard.Root>
            <InfoCard.Label>Área necessária</InfoCard.Label>
            <InfoCard.Value>
              {asBRL(Math.round(Number(summaryData?.required_area)))} m²
            </InfoCard.Value>
          </InfoCard.Root>

          <InfoCard.Root>
            <InfoCard.Label>Potência do kit</InfoCard.Label>
            <InfoCard.Value>
              {asBRL(summaryData?.required_power)} kWp
            </InfoCard.Value>
          </InfoCard.Root>

          <InfoCard.Root>
            <InfoCard.Label>Quantidade de módulos</InfoCard.Label>
            <InfoCard.Value>{asBRL(summaryData?.panels_amount)}</InfoCard.Value>
          </InfoCard.Root>
        </div>

        <div className="flex gap-4">
          <div className="w-[252px]">
            <HomeButton
              variant="outline-tertiary"
              type="button"
              onClick={handleResetSimulation}
            >
              Refazer a simulação
            </HomeButton>
          </div>
          <div className="w-[252px]">
            <Link
              href="/curso-de-energia-solar"
              className="w-full px-4 pt-[13px] pb-[11px] flex items-center justify-center bg-gradient-to-r from-v3-orange-350 to-v3-orange-500 hover:brightness-105 rounded-full transition-all duration-300 font-myriad  text-softBlack text-base leading-none font-semibold uppercase tracking-wider"
            >
              CURSOS DE ENERGIA SOLAR
            </Link>
          </div>
        </div>
      </motion.div>
      <FormContainer headingLevel={headingLevel}>
        {/* MOBILE E TABLET (ou compactMode) */}
        <div className={compactMode ? 'block' : 'lg:hidden'}>
          <div
            className="keen-slider w-full m-auto flex flex-col"
            ref={sliderRef}
          >
            <div className="flex font-myriad">
              <div className="keen-slider__slide flex flex-col gap-1">
                <div className="flex flex-col h-full pb-2">
                  <p className="text-base text-center leading-4 text-v3-gray-475 font-semibold uppercase tracking-wide pb-4">
                    Quantidade de módulos
                  </p>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-2">
                      <p className="text-[2.75rem] text-v3-gray-425 font-semibold">
                        {asBRL(summaryData?.panels_amount)}{' '}
                        <span className="text-sm font-medium">Módulos</span>
                      </p>
                      <p className="text-[0.5rem] leading-[0.6875rem] font-light text-[#868686]">
                        simulação com base em módulos de
                        {asBRL(summaryData?.panels_power)}W
                      </p>
                    </div>

                    <Image
                      src="/assets/v2/solar-plate.png"
                      alt=""
                      width={133}
                      height={154}
                      quality={100}
                      sizes="(max-width: 768px) 100vw,
                          (max-width: 1200px) 50vw,
                          33vw"
                    />
                  </div>
                </div>

                <div className="flex gap-2 px-1 pb-1 font-myriad">
                  <InfoCard.Root>
                    <InfoCard.Label>Potência do kit</InfoCard.Label>
                    <InfoCard.Value>
                      {asBRL(summaryData?.required_power)} kWp
                    </InfoCard.Value>
                  </InfoCard.Root>

                  <InfoCard.Root>
                    <InfoCard.Label>Área necessária</InfoCard.Label>
                    <InfoCard.Value>
                      {asBRL(Math.round(Number(summaryData?.required_area)))} m²
                    </InfoCard.Value>
                  </InfoCard.Root>
                </div>
                <div className="flex gap-2 px-0 pb-1 font-myriad">
                  <InfoCard.Root>
                    <InfoCard.Label>Economia anual estimada</InfoCard.Label>
                    <InfoCard.Value>
                      R$ {asBRL(summaryData?.annual_savings, 2)}
                    </InfoCard.Value>
                  </InfoCard.Root>

                  <InfoCard.Root>
                    <InfoCard.Label>Payback estimado</InfoCard.Label>
                    <InfoCard.Value className="uppercase">
                      {Math.round(summaryData?.payback)} Meses
                    </InfoCard.Value>
                  </InfoCard.Root>
                </div>
                <p className="text-[8px] lg:text-[9px] font-myriad font-light leading-none mt-2 text-v3-gray-475 tracking-wide">
                  *Valores aproximados de acordo com média de mercado. Não
                  configura oferta comercial.
                </p>
              </div>
              <div className="keen-slider__slide flex flex-col gap-1">
                <div className="flex flex-col h-full pb-2">
                  <p className="text-base text-center leading-4 text-v3-gray-475 font-semibold uppercase tracking-wide pb-4">
                    Produção Mensal Estimada
                  </p>

                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-[2.75rem] text-v3-gray-425 font-semibold">
                        {asBRL(
                          Math.round(Number(summaryData?.monthly_production))
                        )}{' '}
                        <span className="text-sm font-medium">kWh/mês</span>
                      </p>
                      <p className="text-[0.5rem] leading-[0.6875rem] font-light text-[#868686]">
                        simulação com base em módulos de{' '}
                        {asBRL(summaryData?.panels_power)}W
                      </p>
                    </div>

                    <Image
                      src="/assets/v2/kit2.png"
                      alt=""
                      width={110}
                      height={169}
                      quality={100}
                      sizes="(max-width: 768px) 100vw,
                          (max-width: 1200px) 50vw,
                          33vw"
                    />
                  </div>
                </div>

                <p className="text-xs leading-4 text-v3-gray-475 uppercase tracking-wide pb-1">
                  VALOR ESTIMADO DO SISTEMA FOTOVOLTAICO
                </p>

                <div className="flex gap-2 px-0 pb-1 font-myriad">
                  <InfoCard.Root>
                    <InfoCard.Label>MENOR</InfoCard.Label>
                    <InfoCard.Value>
                      R$ {asBRL(summaryData?.estimated_generator_cost_min)}
                    </InfoCard.Value>
                  </InfoCard.Root>
                  <InfoCard.Root>
                    <InfoCard.Label>MAIOR</InfoCard.Label>
                    <InfoCard.Value>
                      R$ {asBRL(summaryData?.estimated_generator_cost_max)}
                    </InfoCard.Value>
                  </InfoCard.Root>
                </div>
                <p className="text-[8px] lg:text-[9px] font-myriad font-light leading-none mt-2 text-v3-gray-475 tracking-wide">
                  *Valores aproximados de acordo com média de mercado. Não
                  configura oferta comercial.
                </p>
              </div>
            </div>

            <div className="my-5">
              {loaded && (
                <Dots
                  currentSlide={currentSlide}
                  dotsQuantity={instanceRef.current.track.details.slides.length}
                  onMoveToSlide={instanceRef.current.moveToIdx}
                />
              )}
            </div>

            <div className="flex flex-col gap-5">
              <HomeButton
                type="button"
                variant="outline-tertiary"
                onClick={handleResetSimulation}
              >
                Refazer a simulação
              </HomeButton>

              <Link
                href="/curso-de-energia-solar"
                className="w-full px-4 pt-[13px] pb-[11px] flex items-center justify-center bg-gradient-to-r from-v3-orange-350 to-v3-orange-500 hover:brightness-105 rounded-full transition-all duration-300 font-myriad  text-softBlack text-base leading-none font-semibold uppercase tracking-wider"
              >
                CURSOS DE ENERGIA SOLAR
              </Link>
            </div>
          </div>
        </div>

        {/* DESKTOP + (escondido em compactMode) */}
        <div
          className={
            compactMode
              ? 'hidden'
              : 'hidden lg:flex lg:flex-col lg:w-full lg:h-full lg:justify-between'
          }
        >
          <p className="font-myriad text-center text-lg text-v3-gray-475 tracking-wide uppercase md:text-xl lg:leading-none">
            Produção Mensal Estimada
          </p>
          <div className="w-full flex flex-col justify-between font-myriad">
            <div className="flex justify-between items-end">
              <div className="flex flex-col">
                <p className="text-[3.125rem] leading-none text-v3-gray-425 font-bold 1.5xl:text-[55px]">
                  {asBRL(Math.round(Number(summaryData?.monthly_production)))}
                  <span className="text-lg leading-none font-normal ml-[0.625rem]">
                    kWh/mês
                  </span>
                </p>
                <p className="text-[0.625rem] text-[#B7B7B7]">
                  simulação com base em módulos de{' '}
                  {asBRL(summaryData?.panels_power)}W
                </p>
              </div>

              <div className="relative w-[126px] h-[200px]">
                <Image
                  src="/assets/v2/kit2.png"
                  alt=""
                  fill
                  quality={100}
                  sizes="(max-width: 768px) 100vw,
                      (max-width: 1200px) 50vw,
                      33vw"
                />
              </div>
            </div>
          </div>
          <div className="w-full">
            <p className="text-base leading-4 text-v3-gray-475 uppercase tracking-wide pb-2 xl:text-sm 1.5xl:text-base">
              VALOR ESTIMADO DO SISTEMA FOTOVOLTAICO
            </p>
            <div className="flex gap-6 px-1 pb-0 font-myriad justify-between">
              <div>
                <span className="text-[0.5rem] leading-none text-v3-gray-475 tracking-wide uppercase lg:text-xs lg:leading-none">
                  MENOR
                </span>
                <p className="text-lg leading-6 text-v3-gray-425 font-bold tracking-wide lg:text-xl lg:leading-none xl:text-xl xl:leading-none 1.5xl:text-2xl">
                  R$ {asBRL(summaryData?.estimated_generator_cost_min)}
                </p>
              </div>
              <div>
                <span className="text-[0.5rem] leading-none text-v3-gray-475 tracking-wide uppercase lg:text-xs lg:leading-none">
                  MAIOR
                </span>
                <p className="text-lg leading-6 text-v3-gray-425 font-bold tracking-wide lg:text-xl lg:leading-none xl:text-xl xl:leading-none 1.5xl:text-2xl">
                  R$ {asBRL(summaryData?.estimated_generator_cost_max)}
                </p>
              </div>
            </div>
            <p className="text-[8px] lg:text-[9px] font-myriad font-light leading-none mt-2 text-v3-gray-475 tracking-wide">
              *Valores aproximados de acordo com média de mercado. Não configura
              oferta comercial.
            </p>
          </div>
        </div>
      </FormContainer>
    </div>
  )
}
